import { Field } from 'Components/Core';
import { IFieldProps } from 'Components/Core/Field/types';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ChangeEventWithData } from 'Components/Core/types';
import { IImportTestListDropdownProps, ImportTestListDropdown } from 'Components/Worksheets/Core';
import { SetValuePayLoad } from 'Components/Worksheets/Core/WorksheetEditorInjectedPropsProvider/types';
import { hasValue } from 'Lib/Any';
import { useHasChangedAfterFirstLoad } from 'Lib/Hooks';
import { pathAsArray, pathAsString } from 'Lib/Object/Path/shared';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

type WorksheetBatchUpdateImportSourceFieldProps = Omit<IFieldProps, 'input'> &
  Pick<IImportTestListDropdownProps, 'importPropertyName'> & {
    onChange?: (e: ChangeEventWithData) => void;
    setValue?: (p: SetValuePayLoad) => void;
  };

interface IWorksheetBatchUpdateImportSourceFieldProps extends WorksheetBatchUpdateImportSourceFieldProps {
  queryParams?: any;
}

/**
 * Provides a field for selecting an imported test source.
 *
 * The "name" property is expected to be the ImportedTest property of an AssociatedTestModel.
 *
 * When a test is selected, the test type is also set to be "imported"
 *
 * Currently the test selection is always retrieved for the first worksheet loaded.
 */
let WorksheetBatchUpdateImportSourceField: React.FC<IWorksheetBatchUpdateImportSourceFieldProps> = ({
  value,
  name,
  importPropertyName,
  setValue,
  onChange,
  disabled,
  messages,
  queryParams,
  ...otherProps
}) => {
  const refreshingWorksheets = useSelector((state) => state.worksheetBatchUpdate.status.refreshingWorksheets);
  const worksheetId = useSelector((state) => Object.keys(state.worksheetBatchUpdate.worksheets).find((_) => true));

  // If the worksheet id changes, clear selection as it may not be valid for the new worksheet.
  const worksheetIdHasChanged = useHasChangedAfterFirstLoad(worksheetId);

  useEffect(() => {
    if (worksheetIdHasChanged) {
      setValue!({ name: name!, value: undefined });
      setValue!({ name: pathAsString([...pathAsArray(name!).slice(0, -1), 'AssociatedTestType']), value: undefined });
    }
  }, [worksheetIdHasChanged, name, setValue]);

  if (!hasValue(worksheetId))
    messages?.push({
      message: 'Worksheets must be loaded before an import source can be selected.',
      severity: 'info',
    });

  return (
    <Field
      {...otherProps}
      input={<ImportTestListDropdown />}
      worksheetId={worksheetId}
      disabled={disabled || refreshingWorksheets || !hasValue(worksheetId)}
      placeholder={!hasValue(worksheetId) ? '' : undefined}
      simpleValue
      importPropertyName={importPropertyName}
      // Include worksheetId in key so component is remounted when the worksheet id has changed.
      // TODO: This is a workaround for problems with the ListDropdown component where it doesn't check if it has a valid
      // set of query parameters before trying to run the query
      queryParams={queryParams}
      key={`${name}_${worksheetId}`}
      value={value?.Id ?? null}
      messages={messages}
      searchImmediately={hasValue(worksheetId)}
      onChange={(e: ChangeEventWithData) => {
        setValue!({
          name: pathAsString([...pathAsArray(name!).slice(0, -1), 'AssociatedTestType']),
          value: hasValue(e.data?.value) ? 'imported' : undefined,
        });
        onChange!({
          ...e,
          data: {
            ...e.data,
            value: hasValue(e.data?.value) ? { Id: e.data?.value } : null,
          },
        });
      }}
    />
  );
};

WorksheetBatchUpdateImportSourceField = withInjectedProps(WorksheetBatchUpdateImportSourceField);

export default WorksheetBatchUpdateImportSourceField;
export { WorksheetBatchUpdateImportSourceField };
